import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useCallback, useEffect, useState } from 'react';
import { useAuthStore } from '@/modules/auth/presentation/store/auth.store';
import { authContainer } from '@/modules/auth/di/container';
import type { MenuIndicator, MenuItem } from '@/app/app-module.types';
import { Button } from '@react-apps/design-system';
import {
  useEffectiveRoles,
  useTargetRolesStatus,
} from '@/shared/view-as/use-effective-roles';
import { loadTargetRoles } from '@/shared/impersonation/load-target-roles';
import { AlertIcon, LogoutIcon } from '@/shared/icons';
import { Tooltip } from '@/shared/components/tooltip';
import { MENU_GROUPS, isMenuItemActive, visibleItems } from './sidebar-menu';

// Os grupos que o operador fechou. Sem persistir, a arrumação morreria em todo
// F5 e todo login novo. Seção que deixou de existir fica na lista sem efeito.
const CLOSED_GROUPS_KEY = 'sidebar.closedGroups';

function readClosedGroups(): string[] {
  try {
    const stored: unknown = JSON.parse(
      localStorage.getItem(CLOSED_GROUPS_KEY) ?? '[]',
    );
    return Array.isArray(stored)
      ? stored.filter((section) => typeof section === 'string')
      : [];
  } catch {
    // Storage bloqueado ou valor corrompido: os grupos abrem todos, e só isso.
    return [];
  }
}

interface SidebarProps {
  isCollapsed: boolean;
  isSmScreen: boolean;
  isDrawerOpen: boolean;
  onCloseDrawer: () => void;
}

export function Sidebar({
  isCollapsed,
  isSmScreen,
  isDrawerOpen,
  onCloseDrawer,
}: SidebarProps) {
  const { clearAuth } = useAuthStore();
  const navigate = useNavigate();

  const handleLogout = useCallback(() => {
    void authContainer.repository.clearAuth();
    clearAuth();
    void navigate('/sign-in');
  }, [clearAuth, navigate]);

  if (isSmScreen) {
    return (
      <>
        <div
          className={`fixed inset-0 z-40 bg-black/40 transition-opacity duration-300 ${
            isDrawerOpen ? 'opacity-100' : 'pointer-events-none opacity-0'
          }`}
          onClick={onCloseDrawer}
        />
        <aside
          className={`border-(--color-border) bg-(--color-bg-primary) fixed inset-y-0 left-0 z-50 flex w-64 flex-col border-r shadow-xl transition-transform duration-300 ${
            isDrawerOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
        >
          <SidebarContent collapsed={false} onLogout={handleLogout} />
        </aside>
      </>
    );
  }

  return (
    <aside
      className={`border-(--color-border) bg-(--color-bg-primary) flex flex-col border-r transition-all duration-300 ${
        isCollapsed ? 'w-16' : 'w-64'
      }`}
    >
      <SidebarContent collapsed={isCollapsed} onLogout={handleLogout} />
    </aside>
  );
}

interface SidebarContentProps {
  collapsed: boolean;
  onLogout: () => void;
}

interface NavItemProps {
  item: MenuItem;
  collapsed: boolean;
}

function NavItem(props: NavItemProps) {
  const { indicator } = props.item;
  return indicator ? (
    <SignaledNavItem {...props} indicator={indicator} />
  ) : (
    <NavItemLink {...props} />
  );
}

/** Componente à parte para o hook do indicador rodar em todo render, e só onde há indicador. */
function SignaledNavItem({
  indicator,
  ...props
}: NavItemProps & { indicator: MenuIndicator }) {
  const active = indicator.useActive();
  return (
    <NavItemLink {...props} signal={active ? indicator.label : undefined} />
  );
}

/** `signal` presente = o ponto aparece, e o texto dele vai para o leitor de tela. */
function NavItemLink({
  item,
  collapsed,
  signal,
}: NavItemProps & { signal?: string }) {
  const location = useLocation();
  const isActive = isMenuItemActive(location.pathname, item);

  const link = (
    <NavLink
      to={item.to}
      className={`flex items-center gap-3 overflow-hidden rounded-lg px-3 py-2 text-sm font-medium transition-colors focus-visible:-outline-offset-2 ${
        isActive
          ? 'bg-(--color-accent)/10 text-(--color-accent)'
          : 'text-(--color-text-secondary) hover:bg-(--color-bg-secondary) hover:text-(--color-text-primary)'
      }`}
    >
      {/* O ponto fica no ícone, não no fim da linha: recolhida, a barra só
          mostra o ícone. */}
      <span className="relative flex shrink-0">
        <item.icon className="h-5 w-5" />
        {signal && (
          <span
            aria-hidden="true"
            className="ring-(--color-bg-primary) absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full bg-red-500 ring-2"
          />
        )}
      </span>
      <span
        className={`whitespace-nowrap transition-all duration-300 ${
          collapsed ? 'w-0 opacity-0' : 'opacity-100'
        }`}
      >
        {item.label}
      </span>
      {signal && <span className="sr-only">, {signal}</span>}
    </NavLink>
  );

  if (!collapsed) return link;

  return (
    <Tooltip message={item.label} trigger="hover" triggerClassName="block">
      {link}
    </Tooltip>
  );
}

function SidebarContent({ collapsed, onLogout }: SidebarContentProps) {
  const roles = useEffectiveRoles();
  const targetRolesStatus = useTargetRolesStatus();
  const { pathname } = useLocation();
  const mainItems = visibleItems('main', roles);
  const [closedGroups, setClosedGroups] = useState(readClosedGroups);

  useEffect(() => {
    try {
      localStorage.setItem(CLOSED_GROUPS_KEY, JSON.stringify(closedGroups));
    } catch {
      // Não poder lembrar não é motivo para não fechar.
    }
  }, [closedGroups]);

  return (
    <>
      <div
        data-sidebar="group-label"
        className={`text-(--color-text-secondary) flex shrink-0 items-center overflow-hidden rounded-md px-4 text-xs font-medium outline-none transition-all duration-300 ${
          collapsed ? 'h-0 select-none opacity-0' : 'h-8 opacity-100'
        }`}
      >
        Menu
      </div>

      {/* `min-h-0` deixa o nav encolher abaixo do conteúdo: sem ele a lista não
          rola, é cortada pelo `overflow-hidden` do layout — e o "Sair" junto. */}
      <nav className="flex min-h-0 flex-1 flex-col px-2 py-4">
        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto overflow-x-hidden">
          {/* Sob máscara, o menu é o do alvo. Enquanto as roles dele chegam, o
              do ator não aparece nem por um instante. */}
          {targetRolesStatus === 'pending' && <MenuSkeleton />}
          {targetRolesStatus === 'error' && (
            <TargetRolesError collapsed={collapsed} />
          )}
          {targetRolesStatus !== 'pending' && (
            <div className="space-y-1">
              {mainItems.map((item) => (
                <NavItem key={item.to} item={item} collapsed={collapsed} />
              ))}
            </div>
          )}

          {targetRolesStatus !== 'pending' &&
            MENU_GROUPS.map(({ section, label }) => {
              const items = visibleItems(section, roles);
              if (items.length === 0) return null;
              // Recolhida, a barra não tem rótulo onde clicar para reabrir: fica
              // tudo aberto. E o grupo do item ativo abre à força, senão a rota
              // atual fica sem item marcado à vista.
              const forcedOpen =
                collapsed ||
                items.some((item) => isMenuItemActive(pathname, item));
              const open = forcedOpen || !closedGroups.includes(section);
              return (
                <details
                  key={section}
                  open={open}
                  onToggle={(event) => {
                    // Só o clique do operador diverge do `open` renderizado; o
                    // `toggle` que o próprio React causa não é escolha de ninguém.
                    const isOpen = event.currentTarget.open;
                    if (isOpen === open) return;
                    setClosedGroups((prev) =>
                      isOpen
                        ? prev.filter((closed) => closed !== section)
                        : [...prev, section],
                    );
                  }}
                  className="group space-y-1"
                >
                  <summary
                    // Forçado aberto não fecha: o React só reescreve `open`
                    // quando a prop muda, e o DOM fechado ficaria preso assim.
                    onClick={(event) => forcedOpen && event.preventDefault()}
                    tabIndex={collapsed ? -1 : undefined}
                    aria-hidden={collapsed || undefined}
                    className={`overflow-hidden transition-all duration-300 ${
                      collapsed
                        ? 'pointer-events-none h-0 select-none opacity-0'
                        : 'h-6 opacity-100'
                    } text-(--color-text-secondary) hover:text-(--color-text-primary) flex cursor-pointer list-none items-center px-3 text-xs font-medium uppercase tracking-wider focus-visible:-outline-offset-2`}
                  >
                    {label}
                    <svg
                      className="ml-auto h-3 w-3 shrink-0 transition-transform group-open:rotate-90"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                      aria-hidden="true"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="m8.25 4.5 7.5 7.5-7.5 7.5"
                      />
                    </svg>
                  </summary>
                  {items.map((item) => (
                    <NavItem key={item.to} item={item} collapsed={collapsed} />
                  ))}
                </details>
              );
            })}
        </div>

        <div className="border-(--color-border) border-t pt-3">
          <LogoutButton collapsed={collapsed} onLogout={onLogout} />
        </div>
      </nav>
    </>
  );
}

/** Ocupa a altura de um menu típico, para a barra não pular quando ele chega. */
function MenuSkeleton() {
  return (
    <div role="status" className="space-y-1">
      <span className="sr-only">Carregando o menu do usuário</span>
      {Array.from({ length: 10 }, (_, i) => (
        <div
          key={i}
          aria-hidden="true"
          className="bg-(--color-bg-secondary) h-9 animate-pulse rounded-lg"
        />
      ))}
    </div>
  );
}

/**
 * Sem as roles do alvo, sobram os itens que não exigem role — nunca o menu do
 * ator, que mostraria ao suporte uma tela que o aluno não tem.
 */
function TargetRolesError({ collapsed }: { collapsed: boolean }) {
  if (collapsed) {
    return (
      <Tooltip
        message="Não deu para carregar o menu do usuário. Tentar de novo"
        trigger="hover"
        triggerClassName="block"
      >
        <button
          type="button"
          onClick={() => void loadTargetRoles()}
          aria-label="Não deu para carregar o menu do usuário. Tentar de novo"
          className="flex w-full justify-center rounded-lg px-3 py-2 text-sm font-semibold text-amber-600 hover:bg-amber-50 dark:text-amber-400 dark:hover:bg-amber-950/50"
        >
          <AlertIcon className="h-5 w-5" />
        </button>
      </Tooltip>
    );
  }
  return (
    <div
      role="alert"
      className="space-y-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900 dark:border-amber-700 dark:bg-amber-950/50 dark:text-amber-200"
    >
      <p>Não deu para carregar o menu do usuário.</p>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => void loadTargetRoles()}
      >
        Tentar de novo
      </Button>
    </div>
  );
}

function LogoutButton({
  collapsed,
  onLogout,
}: {
  collapsed: boolean;
  onLogout: () => void;
}) {
  const button = (
    <button
      onClick={onLogout}
      className="flex w-full items-center gap-3 overflow-hidden rounded-lg px-3 py-2 text-sm font-medium text-red-500 transition-colors hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950/50"
      aria-label="Sair"
    >
      <LogoutIcon className="h-5 w-5 shrink-0" />
      <span
        className={`whitespace-nowrap transition-all duration-300 ${
          collapsed ? 'w-0 opacity-0' : 'opacity-100'
        }`}
      >
        Sair
      </span>
    </button>
  );

  if (!collapsed) return button;

  return (
    <Tooltip message="Sair" trigger="hover" triggerClassName="block">
      {button}
    </Tooltip>
  );
}
